import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { updateState } from "../Services/APIService";

interface viewDatas {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface UpdateCardProps {
  viewData: viewDatas | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  stateId: number | "";
  stateName: string;
  isActive: boolean;
}

const UpdateCard: React.FC<UpdateCardProps> = ({
  viewData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    stateId: "",
    stateName: "",
    isActive: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requiredField = <span className="text-danger ms-1">*</span>;

  // Set form data when viewData changes
  useEffect(() => {
    if (viewData) {
      console.log(viewData);
      setFormData({
        stateId: viewData.stateId,
        stateName: viewData.stateName,
        isActive: viewData.isActive,
      });
    }
  }, [viewData]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (formData.stateId === "" || isNaN(Number(formData.stateId))) {
      setError("Please enter a valid State ID");
      return;
    }

    if (!formData.stateName.trim()) {
      setError("Please enter a valid State Name");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID and tenant ID from localStorage
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      // Prepare request payload
      const payload = {
        stateId: Number(formData.stateId),
        stateName: formData.stateName.trim().replace(/\s+/g, " "),
        isActive: formData.isActive,
        modifieddBy: parseInt(userId),
      };

      const result = await updateState(payload);

      if (result.data.isUpdated) {
        toast.success("State Updated Successfully!");
        // Notify parent component that update is complete
        onUpdateComplete();
      } else {
        toast.error(result.data.message || "State updated failed!");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset error state
    setError(null);
    // Call the cancel function
    onCancel();
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Update State</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="stateId" className="form-label">
                State ID
              </label>
              <input
                type="number"
                id="stateId"
                name="stateId"
                className="form-control form-control-lg"
                placeholder="State ID"
                value={formData.stateId}
                disabled // Disable editing of State ID
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="stateName" className="form-label">
                State Name{requiredField}
              </label>
              <input
                type="text"
                id="stateName"
                name="stateName"
                className="form-control form-control-lg"
                placeholder="State Name"
                value={formData.stateName}
                onChange={(e) =>
                  setFormData({ ...formData, stateName: e.target.value })
                }
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                <span className="form-check-label">Is Active</span>
              </label>
            </div>

            <div className="col-12 mt-2">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCard;
